import * as am5 from '@amcharts/amcharts5';
import { Root } from '@amcharts/amcharts5';
import * as am5xy from '@amcharts/amcharts5/xy';
import { Injectable } from '@angular/core';
import { datGrey } from '@dat/datiq-components-pkg/utils';
import { noop } from 'lodash';
import {
    DatiqGraphAxisType,
    DatiqGraphSeriesType,
    DatiqGraphXYTypes,
    IDatiqGraph,
    IDatiqGraphAxis,
    IDatiqGraphSeries,
    IDatiqGraphSeriesServiceType,
} from '../models';
import { DatiqAmcharts5AxisCategoryService } from './datiq-amcharts-5-axis-category.service';
import { DatiqAmcharts5AxisDateService } from './datiq-amcharts-5-axis-date.service';
import { DatiqAmcharts5AxisNumberService } from './datiq-amcharts-5-axis-number.service';
import { DatiqAmcharts5ChartXyMiniature } from './datiq-amcharts-5-chart-xy-miniature.builder';
import { DatiqAmcharts5SeriesCandlestickService } from './datiq-amcharts-5-series-candlestick.service';
import { DatiqAmcharts5SeriesColumnService } from './datiq-amcharts-5-series-column.service';
import { DatiqAmcharts5SeriesLineService } from './datiq-amcharts-5-series-line.service';
import { DatiqAmcharts5SeriesScatterService } from './datiq-amcharts-5-series-scatter.service';
import { datiqAmcharts5SetColor } from './models';

@Injectable({
    providedIn: 'root',
})
export class DatiqAmcharts5ChartXyService {
    constructor(
        private _axisNumber: DatiqAmcharts5AxisNumberService,
        private _axisDate: DatiqAmcharts5AxisDateService,
        private _axisCategory: DatiqAmcharts5AxisCategoryService,
        private _seriesLine: DatiqAmcharts5SeriesLineService,
        private _columnSeries: DatiqAmcharts5SeriesColumnService,
        private _candlestickSeries: DatiqAmcharts5SeriesCandlestickService,
        private _scatterSeries: DatiqAmcharts5SeriesScatterService
    ) {}

    update(root: Root, settings: IDatiqGraph): am5.Chart {
        this.disposeXyChart(root);

        return this.create(root, settings);
    }

    disposeXyChart(root: Root) {
        root?.container?.children?.getIndex(0)?.dispose();
    }

    create(root: Root, settings: IDatiqGraph): am5.Chart {
        const chart = root.container.children.push(
            am5xy.XYChart.new(root, {
                width: am5.percent(100),
                height: am5.percent(100),
            })
        );

        chart.zoomOutButton.set('forceHidden', true);

        this.setAxes(root, chart, settings);
        this.setAllSeries(root, chart, settings);
        this.setXyChartType(root, chart, settings);
        chart.xAxes.each((axis) => {
            this.setCursor(
                chart,
                root,
                axis as am5xy.Axis<am5xy.AxisRendererX>,
                settings
            );
        });
        chart.bottomAxesContainer.set('layout', root.horizontalLayout);
        this.appear(chart);

        return chart;
    }
    setXyChartType(
        root: Root,
        chart: am5xy.XYChart,
        settings: IDatiqGraph
    ): this {
        const updatedChart: am5xy.XYChart =
            settings?.xyType === DatiqGraphXYTypes.Miniature
                ? new DatiqAmcharts5ChartXyMiniature(chart).build()
                : chart;

        return this;
    }
    private appear(chart: am5xy.XYChart) {
        chart?.series?.each((x, i) => x.appear(1000, 100 + i * 100));
    }

    setAllSeries(
        root: Root,
        chart: am5xy.XYChart,
        settings: IDatiqGraph
    ): am5xy.XYChart {
        return settings.series.reduce(
            (
                currentChart: am5xy.XYChart,
                series: IDatiqGraphSeries
            ): am5xy.XYChart => {
                return this.setSeries(root, currentChart, series, settings);
            },
            chart
        );
    }
    setSeries(
        root: Root,
        chart: am5xy.XYChart,
        series: IDatiqGraphSeries,
        settings: IDatiqGraph
    ): am5xy.XYChart {
        const datiqGraphSeriesServices: IDatiqGraphSeriesServiceType = {
            [DatiqGraphSeriesType.Bar]: this._columnSeries,
            [DatiqGraphSeriesType.Line]: this._seriesLine,
            [DatiqGraphSeriesType.Candlestick]: this._candlestickSeries,
            [DatiqGraphSeriesType.Scatter]: this._scatterSeries,
        };
        return (
            datiqGraphSeriesServices[series.type]?.create(
                root,
                chart,
                series,
                settings
            ) ?? chart
        );
    }

    setAxes(
        root: Root,
        chart: am5xy.XYChart,
        settings: IDatiqGraph
    ): am5.Chart {
        this.setYAxes(root, chart, settings);
        this.setXAxes(root, chart, settings);

        return chart;
    }
    setYAxes(
        root: Root,
        chart: am5xy.XYChart,
        settings: IDatiqGraph
    ): am5xy.Axis<am5xy.AxisRendererY>[] {
        return (
            settings?.yAxes?.map(
                (axis: IDatiqGraphAxis): am5xy.Axis<am5xy.AxisRendererY> => {
                    return this.setYAxis(root, chart, axis, settings);
                }
            ) ?? []
        );
    }
    setYAxis(
        root: Root,
        chart: am5xy.XYChart,
        axis: IDatiqGraphAxis,
        settings: IDatiqGraph
    ): am5xy.Axis<am5xy.AxisRendererY> {
        return this.isAxisNumber(axis)
            ? this._axisNumber.createYAxis(root, chart, axis, settings)
            : this.isAxisDate(axis)
            ? this._axisDate.createYAxis(root, chart, axis, settings)
            : null;
    }
    isAxisDate(axis: IDatiqGraphAxis) {
        return axis.type === DatiqGraphAxisType.Date;
    }
    isAxisNumber(axis: IDatiqGraphAxis) {
        return (
            axis.type === DatiqGraphAxisType.Number ||
            axis.type === DatiqGraphAxisType.Dollar
        );
    }
    setXAxes(
        root: Root,
        chart: am5xy.XYChart,
        settings: IDatiqGraph
    ): am5xy.Axis<am5xy.AxisRendererX>[] {
        return (
            settings?.xAxes?.map(
                (axis: IDatiqGraphAxis): am5xy.Axis<am5xy.AxisRendererX> => {
                    return this.setXAxis(root, chart, axis, settings);
                }
            ) ?? []
        );
    }
    setCursor(
        chart: am5xy.XYChart,
        root: Root,
        axis: am5xy.Axis<am5xy.AxisRendererX>,
        settings: IDatiqGraph
    ): this {
        // Current contributions scatterplot has a different dateAxisType from the main series it is plotted with
        // This conditional check fixees it
        // If you have a use case for another scatterplot somewhere, we can adjust the DatiqGraphSeriesType enum

        const seriesWithTooltips = [];
        settings?.series?.map((serie, index) => {
            if (
                serie?.type === DatiqGraphSeriesType.Scatter ||
                serie?.showTooltip
            )
                seriesWithTooltips.push(chart?.series?.getIndex(index));
        });
        const cursor: am5xy.XYCursor = am5xy.XYCursor.new(root, {
            behavior: 'none',
            snapToSeries: seriesWithTooltips,
            snapToSeriesBy: 'xy',
        });

        chart.set(
            'cursor',
            am5xy.XYCursor.new(root, {
                xAxis: axis,
            })
        );

        cursor.lineY.setAll({
            visible: false,
        });

        cursor.lineX.setAll({
            strokeDasharray: null,
            strokeWidth: 1,
            stroke: datiqAmcharts5SetColor(datGrey['500']),
        });

        settings.xyType === DatiqGraphXYTypes.Miniature
            ? noop()
            : cursor.set('xAxis', axis);
        settings.xyType === DatiqGraphXYTypes.Miniature
            ? noop()
            : chart.set('cursor', cursor);

        return this;
    }

    setXAxis(
        root: Root,
        chart: am5xy.XYChart,
        axis: IDatiqGraphAxis,
        settings: IDatiqGraph
    ): am5xy.Axis<am5xy.AxisRendererX> {
        return this.isAxisNumber(axis)
            ? this._axisNumber.createXAxis(root, chart, axis, settings)
            : this.isAxisDate(axis)
            ? this._axisDate.createXAxis(root, chart, axis, settings)
            : this.isCategoryAxis(axis)
            ? this._axisCategory.createXAxis(root, chart, axis, settings)
            : null;
    }
    isCategoryAxis(axis: IDatiqGraphAxis) {
        return axis.type === DatiqGraphAxisType.Category;
    }
}
